import { useEffect } from 'react'
import {
  useInfiniteQuery,
  QueryObserverResult,
  InfiniteData
} from '@tanstack/react-query'
import { fetchNotifications } from '@pepper/utils/src/api/notifications/notifications'

import { useShowMessage } from '../providers'

type ReturnType = {
  data: DataModal[]
  totalRecords: number
  refetch: () => Promise<QueryObserverResult<InfiniteData<DataModal>, unknown>>
  isFetching: boolean
  isFetchingMore: boolean | undefined
  fetchMore: () => void
  canFetchMore: boolean | undefined
  error: Error | null
}

type DataModal = {
  records: UserNotification[]
  total_records: number
  next_page: number | null
}

export const useNotifications = (
  index: any,
  query_params: Partial<NotificationQueryParams>,
  page_size = 10
): ReturnType => {
  const showMessage = useShowMessage()

  const handleApiRequest = async (next_page: number) => {
    const { status, data, error } = await fetchNotifications({
      query_params,
      page: next_page,
      page_size
    })
    if (status === 'ok') {
      return data
    } else {
      throw new Error(error)
    }
  }

  const {
    data,
    isError,
    error,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
    hasNextPage
  } = useInfiniteQuery<DataModal, Error>(
    [index, query_params],
    ({ pageParam }) => handleApiRequest(pageParam),
    {
      getNextPageParam: records => {
        return records.next_page
      }
    }
  )

  useEffect(() => {
    if (isError) {
      showMessage({ type: 'error', message: error?.message })
    }
  }, [isError, error, showMessage])

  return {
    data: data?.pages ?? [],
    totalRecords: data?.pages?.flatMap(record => record.records).length ?? 0,
    refetch,
    isFetching,
    isFetchingMore: isFetchingNextPage,
    fetchMore: fetchNextPage,
    canFetchMore: hasNextPage,
    error
  }
}
